import { Card, Divider, Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import FieldContainer from '../../../FormFields/FieldContainer';
import FormAutocomplete from '../../../FormFields/FormAutocomplete';
import FormDEAField from '../../../FormFields/FormDEANumber';
import FormFileDrop from '../../../FormFields/FormFileDrop';
import FormNPIField from '../../../FormFields/FormNPINumber';
import FormPhoneNumberField from '../../../FormFields/FormPhoneNumField';
import FormTextField from '../../../FormFields/FormTextField';
import FormTitle from '../../../FormFields/FormTitle';
import { FILE_TYPES } from '../../../formFileTypesEnum';
import { OPERATIONS } from '../../../formQuestionsEnum';
import { formCardStyle } from '../../../TransitionStyles';
import { validateAddress, validateEmail, validateZip } from '../../../ValidateFunctions';

MedicalDirectorForm.propTypes = {
  permissions: PropTypes.object,
};

export default function MedicalDirectorForm({ permissions }) {
  const [focusedField, setFocusedField] = useState(null);

  const fieldIds = [
    OPERATIONS.MD_FIRST_NAME.id,
    OPERATIONS.MD_LAST_NAME.id,
    OPERATIONS.MD_ADDRESS.id,
    OPERATIONS.MD_CITY.id,
    OPERATIONS.MD_STATE.id,
    OPERATIONS.MD_ZIP.id,
    OPERATIONS.MD_PHONE_NUMBER.id,
    OPERATIONS.MD_FAX.id,
    OPERATIONS.MD_EMAIL.id,
    OPERATIONS.MD_COMPANY.id,
    OPERATIONS.NPI_NUMBER.id,
    OPERATIONS.DEA_NUMBER.id,
    OPERATIONS.MEDICAL_DIRECTOR_LICENSE.id,
    OPERATIONS.MEDICAL_DIRECTOR_CONTRACT.id,
  ];

  const fiftyStates = useSelector((state) => state.transitions?.data?.fiftyStates);

  const showForm = fieldIds.some((id) => id in permissions);

  return (
    showForm && (
      <Card sx={formCardStyle}>
        <Typography sx={{ mb: 1 }} align="center" variant="body1" color="info.main">
          Medical Director
        </Typography>
        <Divider sx={{ mb: 1, mt: 2, borderWidth: 1 }} />
        <Grid container spacing={1.5}>
          {OPERATIONS.MD_FIRST_NAME.id in permissions && (
            <Grid item xs={12} sm={4}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'First Name' }} />
                <FormTextField
                  fieldName={OPERATIONS.MD_FIRST_NAME.fieldName}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                  fieldAttributes={{
                    isEditable: permissions[OPERATIONS.MD_FIRST_NAME.id],
                  }}
                />
              </FieldContainer>
            </Grid>
          )}
          {OPERATIONS.MD_LAST_NAME.id in permissions && (
            <Grid item xs={12} sm={4}>
              <FormTitle fieldAttributes={{ questionTitle: 'Last Name' }} />
              <FormTextField
                fieldName={OPERATIONS.MD_LAST_NAME.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{
                  isEditable: permissions[OPERATIONS.MD_LAST_NAME.id],
                }}
              />
            </Grid>
          )}
          {OPERATIONS.MD_COMPANY.id in permissions && (
            <Grid item xs={12} sm={4}>
              <FormTitle fieldAttributes={{ questionTitle: 'Company' }} />
              <FormTextField
                fieldName={OPERATIONS.MD_COMPANY.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{
                  isEditable: permissions[OPERATIONS.MD_COMPANY.id],
                }}
              />
            </Grid>
          )}
          {OPERATIONS.MD_ADDRESS.id in permissions && (
            <Grid item xs={12}>
              <FormTitle fieldAttributes={{ questionTitle: 'Address' }} />
              <FormTextField
                fieldName={OPERATIONS.MD_ADDRESS.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{
                  isEditable: permissions[OPERATIONS.MD_ADDRESS.id],
                  validation: {
                    validate: validateAddress,
                  },
                }}
              />
            </Grid>
          )}
          {OPERATIONS.MD_CITY.id in permissions && (
            <Grid item xs={12} sm={4}>
              <FormTitle fieldAttributes={{ questionTitle: 'City' }} />
              <FormTextField
                fieldName={OPERATIONS.MD_CITY.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{
                  isEditable: permissions[OPERATIONS.MD_CITY.id],
                }}
              />
            </Grid>
          )}
          {OPERATIONS.MD_STATE.id in permissions && (
            <Grid item xs={12} sm={4}>
              <FormTitle fieldAttributes={{ questionTitle: 'State' }} />
              <FormAutocomplete
                fieldName={OPERATIONS.MD_STATE.fieldName}
                fieldAttributes={{
                  isEditable: permissions[OPERATIONS.MD_STATE.id],
                }}
                options={fiftyStates || []}
              />
            </Grid>
          )}
          {OPERATIONS.MD_ZIP.id in permissions && (
            <Grid item xs={12} sm={4}>
              <FormTitle fieldAttributes={{ questionTitle: 'Zip' }} />
              <FormTextField
                fieldName={OPERATIONS.MD_ZIP.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{
                  isEditable: permissions[OPERATIONS.MD_ZIP.id],
                  validation: {
                    pattern: { ...validateZip },
                  },
                }}
              />
            </Grid>
          )}
          {OPERATIONS.MD_PHONE_NUMBER.id in permissions && (
            <Grid item xs={12} sm={4}>
              <FormTitle fieldAttributes={{ questionTitle: 'Phone' }} />
              <FormPhoneNumberField
                fieldName={OPERATIONS.MD_PHONE_NUMBER.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{
                  isEditable: permissions[OPERATIONS.MD_PHONE_NUMBER.id],
                }}
              />
            </Grid>
          )}
          {OPERATIONS.MD_FAX.id in permissions && (
            <Grid item xs={12} sm={4}>
              <FormTitle fieldAttributes={{ questionTitle: 'Fax' }} />
              <FormPhoneNumberField
                fieldName={OPERATIONS.MD_FAX.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{
                  isEditable: permissions[OPERATIONS.MD_FAX.id],
                }}
              />
            </Grid>
          )}
          {OPERATIONS.MD_EMAIL.id in permissions && (
            <Grid item xs={12} sm={4}>
              <FormTitle fieldAttributes={{ questionTitle: 'Email' }} />
              <FormTextField
                fieldName={OPERATIONS.MD_EMAIL.fieldName}
                fieldAttributes={{
                  isEditable: permissions[OPERATIONS.MD_EMAIL.id],
                  validation: {
                    pattern: { ...validateEmail },
                  },
                }}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
              />
            </Grid>
          )}
          {OPERATIONS.NPI_NUMBER.id in permissions && (
            <Grid item xs={12} sm={6}>
              <FormTitle fieldAttributes={{ questionTitle: 'NPI#' }} />
              <FormNPIField
                fieldName={OPERATIONS.NPI_NUMBER.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{
                  isEditable: permissions[OPERATIONS.NPI_NUMBER.id],
                }}
              />
            </Grid>
          )}
          {OPERATIONS.DEA_NUMBER.id in permissions && (
            <Grid item xs={12} sm={6}>
              <FormTitle fieldAttributes={{ questionTitle: 'DEA#' }} />
              <FormDEAField
                fieldName={OPERATIONS.DEA_NUMBER.fieldName}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                fieldAttributes={{
                  isEditable: permissions[OPERATIONS.DEA_NUMBER.id],
                }}
              />
            </Grid>
          )}
          {OPERATIONS.MEDICAL_DIRECTOR_LICENSE.id in permissions && (
            <Grid item xs={12} sm={6}>
              <FieldContainer>
                <FormTitle
                  fieldAttributes={{ questionTitle: 'License for facility medical director', fieldType: 'file' }}
                />
                <FormFileDrop
                  fieldName={OPERATIONS.MEDICAL_DIRECTOR_LICENSE.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[OPERATIONS.MEDICAL_DIRECTOR_LICENSE.id],
                    fileTypeId: FILE_TYPES.MEDICAL_DIRECTOR_LICENSE,
                  }}
                />
              </FieldContainer>
            </Grid>
          )}
          {OPERATIONS.MEDICAL_DIRECTOR_CONTRACT.id in permissions && (
            <Grid item xs={12} sm={6}>
              <FieldContainer>
                <FormTitle fieldAttributes={{ questionTitle: 'Upload Contract', fieldType: 'file' }} />
                <FormFileDrop
                  fieldName={OPERATIONS.MEDICAL_DIRECTOR_CONTRACT.fieldName}
                  fieldAttributes={{
                    isEditable: permissions[OPERATIONS.MEDICAL_DIRECTOR_CONTRACT.id],
                    fileTypeId: FILE_TYPES.MEDICAL_DIRECTOR_CONTRACT,
                  }}
                />
              </FieldContainer>
            </Grid>
          )}
        </Grid>
      </Card>
    )
  );
}
